import React, {useCallback, useEffect, useMemo} from "react";
import {matchPath, Redirect, Route, Switch, useHistory, useLocation, useParams} from "react-router-dom";
import {routeFunctions, routes} from "../../../../../core/models/static/routes/app-routes";
import QuoteViews from "./quotes";
import InvoiceViews from "./invoices";
import SalesOrderViews from "./sales-orders";
import CreditViews from "./credits";
import BackOrderViews from "./back-orders";
import TopBarProgressIndicator from "../../../../components/app-specific/topbar-progress-indicator";
import useAccessLevels from "../../../../hooks/use-access-levels";
import ToastService from "../../../../../core/services/toast";
import {generatePath} from "react-router";

const OrderProcessTabs = {
    quotes: 'Quote',
    salesOrders: 'Sales Order',
    backOrders: 'Back Order',
    invoices: 'Invoice',
    credits: 'Credit',
}

const OrderViews = ({allowPayments}) => {
    const operatorAccessLevels = useAccessLevels();
    const location = useLocation();
    const history = useHistory();
    const params = useParams();

    const currentType = useMemo(() => {
        const pathname = location.pathname;
        if (matchPath(pathname, {
            path: [
                routes.sales.base,
                routes.sales.quotes,
                routes.customer.sales.quotes.base,
                routes.customer.sales.quotes.single,
            ],
            exact: true
        })) {
            return OrderProcessTabs.quotes;
        }
        if (matchPath(pathname, {
            path: [
                routes.sales.salesOrder,
                routes.customer.sales.salesOrder.base,
                routes.customer.sales.salesOrder.single,
            ],
            exact: true
        })) {
            return OrderProcessTabs.salesOrders;
        }
        if (matchPath(pathname, {
            path: [
                routes.sales.backOrder,
                routes.customer.sales.backOrder.base,
                routes.customer.sales.backOrder.single,
            ],
            exact: true
        })) {
            return OrderProcessTabs.backOrders;
        }
        if (matchPath(pathname, {
            path: [
                routes.sales.invoices,
                routes.accounting.invoices,
                routes.customer.sales.invoices.base,
                routes.customer.sales.invoices.single,
                routes.customer.accounting.invoices.base,
                routes.customer.accounting.invoices.single,
            ],
            exact: true
        })) {
            return OrderProcessTabs.invoices;
        }
        if (matchPath(pathname, {
            path: [
                routes.sales.credits,
                routes.customer.sales.credits.base,
                routes.customer.sales.credits.single,
            ],
            exact: true
        })) {
            return OrderProcessTabs.credits;
        }
        return OrderProcessTabs.quotes;
    }, [location.pathname])

    /**
     * Checks the pathname of the url with each change of the url. and determines the type of the order
     */
    useEffect(() => {
        if (matchPath(location.pathname, {path: routes.sales.base, exact: true})) {
            if (operatorAccessLevels.packages.sales.quotesList) {
                history.replace(routes.sales.quotes);
            } else if (operatorAccessLevels.packages.sales.salesOrdersList) {
                history.replace(routes.sales.salesOrder);
            } else if (operatorAccessLevels.packages.sales.invoicesList) {
                history.replace(routes.sales.invoices);
            } else if (operatorAccessLevels.packages.sales.creditsList) {
                history.replace(routes.sales.credits);
            } else {
                history.replace(routes.sales.base.concat(routes.error.serverErrorForSection));
            }
        }

        if (matchPath(location.pathname, {path: routes.customer.sales.base, exact: true})) {
            if (operatorAccessLevels.packages.sales.quotesList) {
                history.replace(routeFunctions.customer.sales.quotes.base(params.customerId));
            } else if (operatorAccessLevels.packages.sales.salesOrdersList) {
                history.replace(routeFunctions.customer.sales.salesOrder.base(params.customerId));
            } else if (operatorAccessLevels.packages.sales.invoicesList) {
                history.replace(routeFunctions.customer.sales.invoices.base(params.customerId));
            } else if (operatorAccessLevels.packages.sales.creditsList) {
                history.replace(routeFunctions.customer.sales.credits.base(params.customerId));
            } else {
                history.replace(routes.customer.sales.base.concat(routes.error.serverErrorForSection));
            }
        }
    }, [history, location.pathname, operatorAccessLevels.packages.sales.creditsList, operatorAccessLevels.packages.sales.invoicesList, operatorAccessLevels.packages.sales.quotesList, operatorAccessLevels.packages.sales.salesOrdersList, params.customerId])

    /**
     * Determines if a ball has been passed over relative to their order in ordering process.
     * @param currentType {string}
     * @param type {string}
     * @return {boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case OrderProcessTabs.quotes:
            default:
                return false
            case OrderProcessTabs.salesOrders:
                return (type === OrderProcessTabs.quotes);
            case OrderProcessTabs.backOrders:
                return (type === OrderProcessTabs.quotes || type === OrderProcessTabs.salesOrders);
            case OrderProcessTabs.invoices:
                return (type === OrderProcessTabs.quotes || type === OrderProcessTabs.salesOrders || type === OrderProcessTabs.backOrders);
            case OrderProcessTabs.credits:
                return (type === OrderProcessTabs.quotes || type === OrderProcessTabs.salesOrders || type === OrderProcessTabs.invoices || type === OrderProcessTabs.backOrders);
        }
    }, [])

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        ToastService.mainAPIRedZoneWarning();
    }, [currentType])

    const topSection = useMemo(() =>
        <TopBarProgressIndicator
            types={OrderProcessTabs}
            currentType={currentType}
            passed={passed}
            auto
            className={'sales-views-top-progress-indicator'}
        />, [currentType, passed])

    return (
        <>
            {topSection}
            <Switch>
                <Route path={routes.sales.base} exact/>
                <Route path={routes.customer.sales.base} exact/>

                {/*Old paths*/}
                <Redirect from={routes.salesOld.salesOrder} to={routes.sales.salesOrder} exact push={false}/>
                <Redirect from={routes.salesOld.backOrder} to={routes.sales.backOrder} exact push={false}/>
                <Redirect from={routes.customerSalesOld.backOrder} to={generatePath(routes.customer.sales.backOrder.base, {customerId: params.customerId ?? 0})} exact push={false}/>
                <Redirect from={routes.customerSalesOld.salesOrder} to={generatePath(routes.customer.sales.salesOrder.base, {customerId: params.customerId ?? 0})} exact push={false}/>
                <Redirect from={routes.customerSalesOld.salesOrderDetails}
                          to={generatePath(routes.customer.sales.salesOrder.single, {customerId: params.customerId ?? 0, orderId: params.orderId ?? 0})} exact push={false}/>
                <Redirect from={routes.customerSalesOld.backOrderDetails} to={generatePath(routes.customer.sales.backOrder.single, {customerId: params.customerId ?? 0, orderId: params.orderId ?? 0})}
                          exact push={false}/>

                <Route path={[
                    routes.sales.quotes,
                    routes.customer.sales.quotes.base,
                    routes.customer.sales.quotes.single,
                ]} exact>
                    <QuoteViews/>
                </Route>
                <Route
                    path={[
                        routes.sales.salesOrder,
                        routes.customer.sales.salesOrder.base,
                        routes.customer.sales.salesOrder.single,
                    ]}>
                    <SalesOrderViews/>
                </Route>
                <Route
                    path={[
                        routes.salesOld.backOrder,
                        routes.customerSalesOld.backOrder,
                        routes.customerSalesOld.backOrderDetails,
                        routes.sales.backOrder,
                        routes.customer.sales.backOrder.base,
                        routes.customer.sales.backOrder.single,
                    ]}>
                    <BackOrderViews/>
                </Route>
                <Route
                    path={[
                        routes.sales.invoices,
                        routes.accounting.invoices,
                        routes.customer.sales.invoices.base,
                        routes.customer.sales.invoices.single,
                        routes.customer.accounting.invoices.base,
                        routes.customer.accounting.invoices.single,
                        routes.customer.sales.invoices.base,
                        routes.customer.sales.invoices.single,
                        routes.customer.accounting.invoices.base,
                        routes.customer.accounting.invoices.single,
                    ]}>
                    <InvoiceViews allowPayments={allowPayments}/>
                </Route>
                <Route
                    path={[
                        routes.sales.credits,
                        routes.customer.sales.credits.base,
                        routes.customer.sales.credits.single,
                    ]}>
                    <CreditViews/>
                </Route>
                <Redirect
                    to={
                        params.customerId
                            ? routes.customer.sales.base.concat(routes.error.serverErrorForSection)
                            : routes.sales.base.concat(routes.error.serverErrorForSection)}
                />
            </Switch>
        </>

    );
};

export default OrderViews;
